import React, { useState, useRef, useEffect, useMemo } from "react";
import { Input } from "./input";
import { cn } from "@/lib/utils";

interface AutocompleteInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  required?: boolean;
  className?: string;
}

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  id,
  value,
  onChange,
  options,
  placeholder,
  required = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // Inline completion is derived to avoid flicker from state churn
  // We keep the overlay mounted at all times to prevent flashing
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Filter options
  useEffect(() => {
    const filtered = options.filter((option) =>
      option.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredOptions(filtered);
    setHighlightedIndex(-1);
  }, [value, options]);

  // Compute inline completion without using state to avoid transient empty renders
  const inlineCompletion = useMemo(() => {
    if (!value || value.length === 0) return "";
    const match = options.find((option) =>
      option.toLowerCase().startsWith(value.toLowerCase())
    );
    if (!match) return "";
    if (match.toLowerCase() === value.toLowerCase()) return "";
    return match.slice(value.length);
  }, [options, value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setIsOpen(true);
  };

  const handleOptionClick = (option: string) => {
    onChange(option);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const acceptInlineCompletion = () => {
    if (inlineCompletion && value.length > 0) {
      // Find the original option to preserve proper casing
      const exactMatch = options.find((option) =>
        option.toLowerCase().startsWith(value.toLowerCase())
      );
      if (exactMatch) {
        onChange(exactMatch);
      } else {
        // Fallback to just appending the completion
        onChange(value + inlineCompletion);
      }
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle Tab for inline completion
    if (e.key === "Tab" && inlineCompletion) {
      // Accept the completion but don't prevent default
      // This allows Tab to both complete and move to next field
      const exactMatch = options.find((option) =>
        option.toLowerCase().startsWith(value.toLowerCase())
      );
      if (exactMatch) {
        onChange(exactMatch);
      } else {
        onChange(value + inlineCompletion);
      }
      setIsOpen(false);
      // Don't preventDefault - let Tab continue to next field
      return;
    }

    if (!isOpen) {
      if (e.key === "ArrowDown") {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleOptionClick(filteredOptions[highlightedIndex]);
        } else if (inlineCompletion) {
          acceptInlineCompletion();
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case "Tab":
        if (!inlineCompletion) {
          setIsOpen(false);
        }
        break;
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Don't close if clicking on an option
    if (listRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsOpen(false);
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  return (
    <div className="relative">
      {/* Input with inline completion overlay */}
      <div className="relative">
        <Input
          ref={inputRef}
          id={id}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          required={required}
          className={cn(
            // Ensure height/line-height/padding are consistent across states
            "relative z-10 bg-transparent h-10 min-h-10 px-3 py-2 text-base md:text-sm leading-[1.5rem]",
            // Neutralize focus ring offset so layout doesn't shift
            "focus-visible:ring-0 focus-visible:ring-offset-0",
            className
          )}
          autoComplete="off"
        />

        {/* Inline completion overlay */}
        <div
          className="absolute inset-[1px] flex items-center px-3 py-2 text-base md:text-sm pointer-events-none"
          style={{
            lineHeight: "1.5rem",
            fontFamily: "inherit",
          }}
          aria-hidden
        >
          <span className="text-transparent select-none">{value}</span>
          {inlineCompletion ? (
            <span className="text-gray-400 dark:text-gray-500 select-none">
              {inlineCompletion}
            </span>
          ) : null}
        </div>
      </div>

      {isOpen && filteredOptions.length > 0 && (
        <ul
          ref={listRef}
          className="absolute z-50 w-full mt-1 max-h-60 overflow-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg"
        >
          {filteredOptions.map((option, index) => (
            <li
              key={option}
              className={cn(
                "px-3 py-2 cursor-pointer text-sm text-gray-900 dark:text-gray-100",
                index === highlightedIndex
                  ? "bg-blue-50 dark:bg-blue-900/50 text-blue-900 dark:text-blue-200"
                  : "hover:bg-gray-50 dark:hover:bg-gray-700"
              )}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur
                handleOptionClick(option);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {option}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AutocompleteInput;
